import { ClerkProvider, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { useEffectOnce } from '@custom-react-hooks/use-effect-once';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssVarsDesignTokenProvider } from 'css-vars-design-token';
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { OnCatchError } from './client-app/components/ErrorBoundaries';
import { ErrorScreen } from './client-app/components/ErrorScreen';
import { FacebookPixel } from './client-app/components/FacebookPixel';
import { Footer } from './client-app/components/Footer';
import { GooglePixel } from './client-app/components/GooglePixel';
import { Header } from './client-app/components/Header';
import { Placeholder } from './client-app/components/Placeholder';
import { themes } from './client-app/components/themes';
import {
  TutorLayout,
  TutorPrimaryPanel,
  TutorSecondaryPanel,
} from './client-app/components/TutorPanels';
import { useRudderStackAnalytics } from './client-app/hooks/use-analytics';
import {
  AuthedUserContextProvider,
  useAuthedUserContext,
} from './client-app/hooks/use-authed-user';
import { Router } from './client-app/router';
import { UnAuthRouter } from './client-app/unauth-router';
import { loadCatalog } from './client-app/util/i18n';
import { serviceWorkerManager } from './client-app/util/service-worker';
import './css/index.css';
import { UserType } from './ifaces/user';
import { clearClerkAuthToken, setClerkAuthToken } from './client-app/util/clerk';

const queryClient = new QueryClient();

// Providers
export function Application() {
  useEffectOnce(() => {
    loadCatalog('en');
    // @ts-expect-error Vite in development
    if (import.meta.env.DEV) serviceWorkerManager.register();
  });
  // Preload analytics for all pages including landing page
  useRudderStackAnalytics();

  const [hasUncaughtError, setHasUncaughtError] = useState(false);
  return (
    <OnCatchError handleError={() => setHasUncaughtError(true)}>
      {hasUncaughtError ? (
        <ErrorScreen message="unexpected global error" />
      ) : (
        <I18nProvider i18n={i18n}>
          {/* @ts-expect-error Reference a different build system variable */}
          <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}>
            <QueryClientProvider client={queryClient}>
              <CssVarsDesignTokenProvider themes={themes} theme="light">
                <BrowserRouter>
                  <MainScreen />
                </BrowserRouter>
              </CssVarsDesignTokenProvider>
            </QueryClientProvider>
          </ClerkProvider>
        </I18nProvider>
      )}
    </OnCatchError>
  );
}

// Screen loader
function MainScreen() {
  const auth = useAuth();
  const [token, setToken] = useState('');
  const updateToken = useCallback(() => {
    auth.getToken().then((token) => {
      if (!token) {
        clearClerkAuthToken();
      } else {
        setToken(token);
        setClerkAuthToken(token);
      }
    });
  }, [auth]);
  useEffect(() => {
    if (auth.isLoaded && auth.isSignedIn && !token) updateToken();
  }, [auth, token, updateToken]);
  useEffect(() => {
    if (auth.isLoaded && !auth.isSignedIn) clearClerkAuthToken();
  }, [auth, token]);
  return (
    <div className="screen">
      <Header />
      <main>
        <SignedOut>
          <UnAuthRouter />
          <FacebookPixel authedUser={false} />
          <GooglePixel authedUser={false} />
        </SignedOut>
        <SignedIn>
          <ContentLoader />
        </SignedIn>
      </main>
      <Footer />
    </div>
  );
}

function ContentLoader() {
  return (
    <AuthedUserContextProvider>
      <AuthedContent />
    </AuthedUserContextProvider>
  );
}

function AuthedContent() {
  const auth = useAuthedUserContext();
  const analytics = useRudderStackAnalytics();
  useEffect(() => {
    if (auth.ready && auth.user_id && analytics) {
      const authTracked = localStorage.getItem('auth-tracked');
      if (!authTracked) {
        analytics?.identify(
          auth?.user_id,
          {
            name: auth.profile?.full_name || '',
            email: auth.email,
          },
          {},
          () => {},
        );
        analytics?.track(
          'Signed In',
          {
            app: 'ProfAI',
            email: auth.email,
          },
          {
            userId: auth.user_id,
          },
        );
        localStorage.setItem('auth-tracked', 'set');
      }
    }

    const handleBeforeUnload = () => {
      localStorage.removeItem('auth-tracked');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [analytics, auth]);

  if (!auth.ready) {
    return (
      <TutorLayout>
        <TutorPrimaryPanel pointer unanimated>
          <Trans>Loading...</Trans>
        </TutorPrimaryPanel>
      </TutorLayout>
    );
  }

  return auth.error ? (
    <ErrorScreen message="Authentication failed" />
  ) : auth.ready ? (
    <>
      <Router />
      {auth.user_type === UserType.Consumer && (
        <>
          <FacebookPixel authedUser={true} />
          <GooglePixel authedUser={true} />
        </>
      )}
    </>
  ) : (
    <TutorLayout>
      <TutorPrimaryPanel pointer unanimated>
        <Trans>Authenticating...</Trans>
      </TutorPrimaryPanel>
      <TutorSecondaryPanel unanimated>
        <Placeholder />
      </TutorSecondaryPanel>
    </TutorLayout>
  );
}
